import { ColumnBoxFlexFullWidth } from '../common/boxes';
import FreelancersTableWithSearch from './FreelancersTableWithSearch';
import { useCallback, useMemo, useState } from 'react';
import { Loader } from '../common/Loader';
import { useQueryParamsState } from '../../hooks/useQueryParamsState';
import { PAGINATION_SIZE_OPTIONS } from '../../core/pagination';
import { useGetFreelancersList } from '../../queryHooks/freelancer/useGetFreelancersList';
import { CustomerStatus } from '../../enums/customerStatus';
import { IFreelancerSearchRequest, IFreelancerSearchResponse } from '../../interfaces/freelancerSearch';
import { ISearchHeaderInputProps } from '../../interfaces/searchHeaderProps';
import { getStateFromSearchInputProps } from '../../utils/getStateFromSearchInputProps';
import { useSearchParams } from 'react-router-dom';
import BullhornProfileModal from './BullhornProfileModal';
import { ITableAction } from '../../interfaces/tableProps';
import { AccountBox } from '@mui/icons-material';

const searchInputProps: ISearchHeaderInputProps[] = [
  { name: 'id', label: 'ID', sx: { width: 100 }, isNumber: true },
  { name: 'email', label: 'Email' },
  { name: 'firstName', label: 'First Name' },
  { name: 'lastName', label: 'Last Name' },
  { name: 'country', label: 'Country' },
  { name: 'town', label: 'Town' },
  { name: 'phone', label: 'Phone' },
  {
    name: 'status', label: 'Status', select: {
      list: Object.values(CustomerStatus),
      getLabel: (v) => v,
      getValue: (v) => v
    }
  },
  { name: 'fromDate', label: 'From Date', isDatePicker: true },
  { name: 'toDate', label: 'To Date', isDatePicker: true }
];

export const freelancersInitialState: IFreelancerSearchRequest = getStateFromSearchInputProps<IFreelancerSearchRequest>(searchInputProps);
export const BULLHORN_PROFILE_QUERY_PARAM = 'bullhorn';

const Freelancers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const bullhornId = useMemo(() => searchParams.get(BULLHORN_PROFILE_QUERY_PARAM) || '', [searchParams]);
  const [searchState, setSearchState] = useState<IFreelancerSearchRequest>(freelancersInitialState);
  const [totalCount, setTotalCount] = useState(0);

  const { queryParamState, isPrePopulated } = useQueryParamsState({
    state: searchState,
    setState: setSearchState,
    wildcards: ['email', 'firstName', 'lastName', 'phone'],
    parser: { size: (val: string) => PAGINATION_SIZE_OPTIONS.includes(+val) ? +val : PAGINATION_SIZE_OPTIONS[0] }
  });

  const { data, isFetching } = useGetFreelancersList({
    queryParamState: queryParamState,
    onSuccess: (data) => setTotalCount(data.size * data.totalPages - (data.size - data.totalElements))
  });

  const bullhornProfile = useMemo(() => data?.content.find(f => f.id === +bullhornId), [data, bullhornId]);
  const closeBullhornModal = useCallback(() => {
    searchParams.delete(BULLHORN_PROFILE_QUERY_PARAM);
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const openBullhornModal = useCallback((customerId: number) => {
    searchParams.set(BULLHORN_PROFILE_QUERY_PARAM, customerId.toString());
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const tableActions = useMemo<ITableAction[]>(() => ([
    {
      icon: <AccountBox color={'primary'}/>,
      tooltip: 'Bullhorn Profile',
      onClick: (user: IFreelancerSearchResponse) => openBullhornModal(user.id)
    }
  ]), [openBullhornModal]);

  return (
    <ColumnBoxFlexFullWidth>
      {
        isFetching && <Loader/>
      }
      {
        data && isPrePopulated &&
        <ColumnBoxFlexFullWidth>
          <FreelancersTableWithSearch
            searchInputProps={searchInputProps}
            tableData={{
              list: data.content,
              pagination: {
                size: searchState.size,
                page: searchState.page,
                setState: setSearchState,
                sizeOptions: PAGINATION_SIZE_OPTIONS,
                totalCount: totalCount
              }
            }}
            searchProps={{
              initialState: freelancersInitialState,
              params: searchState,
              setParams: setSearchState
            }}
            tableActions={tableActions}
          />
        </ColumnBoxFlexFullWidth>
      }
      {
        bullhornProfile &&
        <BullhornProfileModal onClose={closeBullhornModal} freelancer={bullhornProfile}/>
      }
    </ColumnBoxFlexFullWidth>
  );
};

export default Freelancers;